/**
 * ERTS prediction service — fetches the ML-projected ERTS for an event.
 *
 * The ERTS Prediction Pipeline runs inference every five minutes against
 * IN_WORK maintenance events and stores one projection per event. This service
 * reads that projection through the AETHER backend (which proxies to the
 * predictions table with least-privilege, read-only access) so the frontend
 * never holds a cross-service credential.
 */
import { UnscheduledEvent } from '../types';
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';
import type {
  ErtsPredictionRequestResult,
  ErtsPredictionSummary,
  PredictionBarrierRequest,
  PredictionBarrierType,
  PredictionDefectRequest,
  PredictionGetReceipt,
  PredictionPostReceipt,
  PredictionRequest,
} from './erts-inference-types';

export type { ErtsPredictionRequestResult, ErtsPredictionSummary } from './erts-inference-types';

const logger = createLogger('ErtsPredictionService');
const ERTS_INFERENCE_API_URL: string = process.env.REACT_APP_ERTS_INFERENCE_API_URL || '';
const OPERATION_TIMEOUT_MS: number = 30_000;
const POLL_INTERVAL_MS: number = 500;
const MAX_DEFECTS: number = 40;

const PREDICTION_BARRIER_TYPES: Readonly<Record<string, PredictionBarrierType>> = {
  Parts: 'Parts',
  Tooling: 'Tooling',
  Manpower: 'Manpower',
  Equipment: 'Equipment',
  Engineering: 'Other',
  'Dark Hours': 'Dark Hours',
  Other: 'Other',
};

/** Extracts a log-safe message from an unknown rejection value. */
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

/**
 * True when a rejection is the deliberate abort raised by the operation deadline
 * in {@link requestErtsPrediction} rather than a real fault. An abort is the
 * expected outcome of a slow projection, so it must not be logged as a failure.
 */
function isDeadlineAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Records a failed step of the prediction round-trip, staying silent for the
 * expected deadline abort.
 */
function logPredictionStepFailure(step: string, error: unknown): void {
  if (isDeadlineAbort(error)) return;
  logger.warn('ERTS prediction step failed', { step, error: errorMessage(error) });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isNumberRecord(value: unknown): value is Record<string, number> {
  return (
    isObject(value) &&
    Object.values(value).every(
      (entry: unknown): boolean => typeof entry === 'number' && Number.isFinite(entry),
    )
  );
}
function parsePostReceipt(value: unknown): PredictionPostReceipt | null {
  if (!isObject(value) || !Array.isArray(value.defects)) return null;
  if (
    typeof value.eventId !== 'string' ||
    typeof value.sourceRevision !== 'number' ||
    !Number.isInteger(value.sourceRevision) ||
    typeof value.rejectedDefectCount !== 'number' ||
    !isNumberRecord(value.rejectedByReason) ||
    typeof value.replayed !== 'boolean' ||
    typeof value.requestId !== 'string'
  )
    return null;
  return {
    eventId: value.eventId,
    sourceRevision: value.sourceRevision,
    servedDefectCount: value.defects.length,
    rejectedDefectCount: value.rejectedDefectCount,
    rejectedByReason: value.rejectedByReason,
    replayed: value.replayed,
    requestId: value.requestId,
  };
}
function parseGetReceipt(
  value: unknown,
  expectedDefectIds: ReadonlySet<number>,
): PredictionGetReceipt | null {
  if (!isObject(value) || !isObject(value.event) || !Array.isArray(value.defects)) return null;
  if (typeof value.eventId !== 'string' || typeof value.requestId !== 'string') return null;
  const sourceRevision: unknown = value.event.source_revision;
  if (typeof sourceRevision !== 'number' || !Number.isInteger(sourceRevision)) return null;
  const defects: Record<string, string> = {};
  for (const defect of value.defects) {
    if (!isObject(defect)) return null;
    const defectId: unknown = defect.defect_id;
    const predictedReturnZulu: unknown = defect.predicted_return_zulu;
    if (
      typeof defectId !== 'number' ||
      !Number.isInteger(defectId) ||
      !expectedDefectIds.has(defectId) ||
      typeof predictedReturnZulu !== 'string' ||
      Number.isNaN(Date.parse(predictedReturnZulu)) ||
      defects[String(defectId)] !== undefined
    )
      return null;
    defects[String(defectId)] = predictedReturnZulu;
  }
  return {
    eventId: value.eventId,
    sourceRevision,
    updatedAt: typeof value.event.updated_at === 'string' ? value.event.updated_at : '',
    requestId: value.requestId,
    prediction: { latest: '', defects },
  };
}
function ataChapter(value: string | undefined): number {
  const match: RegExpMatchArray | null = value?.trim().match(/^(\d{1,2})(?:\D|$)/) || null;
  const chapter: number = match ? Number(match[1]) : 0;
  return chapter >= 0 && chapter <= 99 ? chapter : 0;
}
function buildBarrierRequest(
  barrier: NonNullable<UnscheduledEvent['barriers']>[number],
  defectId: number,
): PredictionBarrierRequest {
  const type: PredictionBarrierType | undefined = PREDICTION_BARRIER_TYPES[barrier.type];
  if (!type) throw new Error('Unsupported maintenance barrier type');
  const request: PredictionBarrierRequest = {
    barrier_id: barrier.id,
    defect_id: defectId,
    type,
    start_time: barrier.startTime,
    deleted: false,
  };
  if (barrier.endTime) request.end_time = barrier.endTime;
  return request;
}
function buildDefectRequest(
  event: UnscheduledEvent,
  defect: NonNullable<UnscheduledEvent['defects']>[number],
): PredictionDefectRequest {
  const request: PredictionDefectRequest = {
    defect_id: defect.id,
    ata_chapter: ataChapter(event.selectedChapters?.[defect.id]),
    defect_text: defect.text,
    deleted: false,
    barriers: (event.barriers || [])
      .filter((barrier): boolean => barrier.defectId === defect.id)
      .map((barrier): PredictionBarrierRequest => buildBarrierRequest(barrier, defect.id)),
  };
  if (defect.defectCompletionDate) request.defect_completion_date = defect.defectCompletionDate;
  if (defect.defectCompletionTime) request.defect_completion_time = defect.defectCompletionTime;
  return request;
}
function buildPredictionRequest(event: UnscheduledEvent): PredictionRequest {
  const defects: NonNullable<UnscheduledEvent['defects']> = event.defects || [];
  const revision: number = event.version ?? event.revisionCounter;
  if (defects.length === 0 || defects.length > MAX_DEFECTS) {
    throw new Error('Event must contain between 1 and 40 defects');
  }
  if (!Number.isInteger(revision) || revision < 0) throw new Error('Event revision is invalid');
  return {
    event_id: event.eventId,
    idempotency_key: `aether:${event.eventId}:${revision}`.slice(0, 128),
    revision_counter: revision,
    tail_number: event.tailNumber,
    station: event.station,
    time_declared_zulu: event.timeDeclaredZulu,
    status: event.status,
    defects: defects.map((defect): PredictionDefectRequest => buildDefectRequest(event, defect)),
  };
}
function inferenceFetch(
  path: string,
  signal: AbortSignal,
  method: 'GET' | 'POST' = 'GET',
  body?: string,
): Promise<Response | null> {
  return authedFetch(path, {
    method,
    body,
    includeContentType: method === 'POST',
    baseUrl: ERTS_INFERENCE_API_URL,
    signal,
    authorizationFormat: 'raw',
    redirectOnUnauthorized: false,
  });
}

/**
 * Submits the prediction request, retrying once on a 5xx. Never rejects: a
 * transport fault, an unparseable body, and the deadline abort all resolve to
 * `null`, which the caller treats as "no projection available". The sequential
 * awaits share one handler because every failure has the same outcome.
 */
async function postPrediction(
  request: PredictionRequest,
  signal: AbortSignal,
): Promise<PredictionPostReceipt | null> {
  try {
    for (let attempt: number = 0; attempt < 2; attempt += 1) {
      const body: string = JSON.stringify(request);
      const response: Response | null = await inferenceFetch('/predictions', signal, 'POST', body);
      if (!response) return null;
      if (response.status === 500 && attempt === 0) continue;
      if (!response.ok) {
        logger.warn('ERTS prediction POST failed', { status: response.status });
        return null;
      }
      return parsePostReceipt(await response.json());
    }
    return null;
  } catch (error: unknown) {
    logPredictionStepFailure('postPrediction', error);
    return null;
  }
}

/**
 * Polls until the stored projection reaches the revision that was just posted.
 * Never rejects. Faults, and the deadline abort that ends the poll loop, all
 * resolve to `null`. The sequential awaits share one handler because every
 * failure has the same outcome.
 */
async function pollPrediction(
  eventId: string,
  expectedRevision: number,
  expectedDefectIds: ReadonlySet<number>,
  signal: AbortSignal,
): Promise<PredictionGetReceipt | null> {
  try {
    while (!signal.aborted) {
      const response: Response | null = await inferenceFetch(
        `/events/${encodeURIComponent(eventId)}`,
        signal,
      );
      if (!response) return null;
      if (response.ok) {
        const receipt: PredictionGetReceipt | null = parseGetReceipt(
          await response.json(),
          expectedDefectIds,
        );
        if (!receipt || receipt.eventId !== eventId || receipt.sourceRevision === null) return null;
        if (receipt.sourceRevision > expectedRevision) return null;
        if (receipt.sourceRevision === expectedRevision) return receipt;
      } else if (response.status !== 404 && response.status !== 500) {
        return null;
      }
      await new Promise<void>((resolve: () => void): void => {
        window.setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }
    return null;
  } catch (error: unknown) {
    logPredictionStepFailure('pollPrediction', error);
    return null;
  }
}

/**
 * Runs the post-then-poll round-trip and verifies the projection belongs to the
 * revision that was submitted. Never rejects: a rejected request shape from
 * {@link buildPredictionRequest} or a failed step resolves to `null`.
 */
async function runPrediction(
  event: UnscheduledEvent,
  signal: AbortSignal,
): Promise<ErtsPredictionRequestResult | null> {
  try {
    const request: PredictionRequest = buildPredictionRequest(event);
    const postReceipt: PredictionPostReceipt | null = await postPrediction(request, signal);
    if (
      !postReceipt ||
      postReceipt.eventId !== request.event_id ||
      postReceipt.sourceRevision !== request.revision_counter
    )
      return null;
    const expectedDefectIds: ReadonlySet<number> = new Set(
      request.defects.map((defect): number => defect.defect_id),
    );
    const getReceipt: PredictionGetReceipt | null = await pollPrediction(
      postReceipt.eventId,
      postReceipt.sourceRevision,
      expectedDefectIds,
      signal,
    );
    if (!getReceipt) return null;
    return {
      servedDefectCount: postReceipt.servedDefectCount,
      rejectedDefectCount: postReceipt.rejectedDefectCount,
      rejectedByReason: postReceipt.rejectedByReason,
      replayed: postReceipt.replayed,
      prediction: getReceipt.prediction,
    };
  } catch (error: unknown) {
    if (!isDeadlineAbort(error)) {
      logger.error('ERTS prediction run failed', {
        eventId: event.eventId,
        error: errorMessage(error),
      });
    }
    return null;
  }
}

/** Requests and verifies an immediate projection after an Unscheduled update. */
export async function requestErtsPrediction(
  event: UnscheduledEvent,
): Promise<ErtsPredictionRequestResult | null> {
  if (!ERTS_INFERENCE_API_URL) return null;
  const controller: AbortController = new AbortController();
  let timeoutId: number = 0;
  const deadline: Promise<null> = new Promise<null>((resolve: (value: null) => void): void => {
    timeoutId = window.setTimeout((): void => {
      controller.abort();
      resolve(null);
    }, OPERATION_TIMEOUT_MS);
  });
  try {
    return await Promise.race([runPrediction(event, controller.signal), deadline]);
  } catch (error: unknown) {
    logger.error('ERTS prediction request failed', {
      eventId: event.eventId,
      error: errorMessage(error),
    });
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * A single defect's ML-projected ERTS within an event.
 */
export interface DefectErtsPrediction {
  defectId: string;
  predictedReturnZulu: string;
  predErtsHours: number;
  predP10Hours: number;
  predP90Hours: number;
  trustLabel: string;
  route: string;
  ataChapter: string;
}

/**
 * The ML-projected ERTS for a single maintenance event. Hours are measured from
 * the event's time-declared timestamp; `predictedReturnZulu` is the absolute
 * projected return time as an ISO-8601 Zulu string. `defectPredictions` carries
 * the per-defect breakdown keyed by defect id; the top-level fields mirror the
 * latest projection across defects.
 */
export interface ErtsPrediction {
  eventId: string;
  predictedReturnZulu: string;
  predErtsHours: number;
  predP10Hours: number;
  predP90Hours: number;
  trustLabel: string;
  route: string;
  updatedAt: string;
  defectPredictions?: Record<string, DefectErtsPrediction>;
}

/**
 * Fetches the projected ERTS for a single event. Returns null when no active
 * prediction exists (404), the event is closed, or the request fails — callers
 * treat a null result as "no projection available" and show no projected date.
 */
export async function fetchErtsPrediction(eventId: string): Promise<ErtsPrediction | null> {
  if (!eventId) return null;

  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/erts-prediction`);
    if (!response) return null;
    if (response.status === 404) return null;
    if (!response.ok) {
      logger.warn('Failed to fetch ERTS prediction', { status: response.status, eventId });
      return null;
    }
    return (await response.json()) as ErtsPrediction;
  } catch (err: unknown) {
    logger.error('fetchErtsPrediction failed', { error: errorMessage(err), eventId });
    return null;
  }
}

/**
 * Fetches projected return times for all active events in a single call,
 * returning a map of `eventId -> { latest, defects }` where `latest` is the
 * event-level latest projected return (ISO-8601 Zulu) and `defects` maps each
 * defect id to its projected return. Used to populate the homepage Projected
 * ERTS column without one request per row. Returns an empty map on any failure
 * so the list still renders.
 */
export async function fetchActiveErtsPredictions(): Promise<Record<string, ErtsPredictionSummary>> {
  try {
    const response = await authedFetch('/erts-predictions');
    if (!response || !response.ok) {
      if (response)
        logger.warn('Failed to fetch ERTS predictions batch', { status: response.status });
      return {};
    }
    const data = await response.json();
    return (data.predictions as Record<string, ErtsPredictionSummary>) || {};
  } catch (err: unknown) {
    logger.error('fetchActiveErtsPredictions failed', { error: errorMessage(err) });
    return {};
  }
}
